import {
  Badge,
  Button,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast
} from "@veritrellis/ui";
import type { WorkspaceRole } from "@veritrellis/shared/roles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate, Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { RoleCapabilityMatrix } from "../components/access/RoleCapabilityMatrix.js";
import { apiFetch } from "../lib/api.js";
import { capabilityLabel, sortCapabilityKeys } from "../lib/capabilityPresentation.js";
import {
  workspaceRoleBadgeVariant,
  workspaceRoleDescriptions,
  workspaceRolesOrdered
} from "../lib/workspaceRolePresentation.js";
import { useAccessManifest } from "../hooks/useAccessManifest.js";
import { roleAtLeast, useCurrentRole } from "../hooks/useCurrentRole.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

export const SettingsAccessPage = () => {
  const { workspaceId, isLoading: widLoading } = useWorkspaceId();
  const { role } = useCurrentRole();
  const manifestQ = useAccessManifest(workspaceId);
  const queryClient = useQueryClient();
  const canViewMatrix = roleAtLeast(role, "admin");

  const patchStrict = useMutation({
    mutationFn: async (value: boolean) => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/access/strict-production-separation`,
        {
          method: "PATCH",
          body: JSON.stringify({ value })
        }
      );
      if (!res.ok) {
        throw new Error(`strict_${String(res.status)}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["access", "manifest", workspaceId] });
      toast.show({
        title: "Saved",
        message: "Strict production separation updated.",
        color: "green"
      });
    },
    onError: () => {
      toast.show({ title: "Error", message: "Could not update setting.", color: "red" });
    }
  });

  const migration = useMutation({
    mutationFn: async (mapViewerToAuditor: boolean) => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/access/migration/apply`, {
        method: "POST",
        body: JSON.stringify({ map_viewer_to_auditor: mapViewerToAuditor })
      });
      if (!res.ok) {
        throw new Error(`migration_${String(res.status)}`);
      }
      return (await res.json()) as { updated: number };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["members", workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "me"] });
      toast.show({
        title: "Migration applied",
        message: `Updated ${String(data.updated)} memberships.`,
        color: "green"
      });
    },
    onError: () => {
      toast.show({ title: "Error", message: "Migration failed.", color: "red" });
    }
  });

  if (widLoading) {
    return <p className="text-sm">Loading…</p>;
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }
  if (manifestQ.isLoading) {
    return (
      <div className="flex min-h-[160px] items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (manifestQ.isError || manifestQ.data === undefined) {
    return <p className="text-sm text-destructive">Could not load access manifest.</p>;
  }

  const m = manifestQ.data.manifest;
  const capabilityKeys = sortCapabilityKeys(Object.keys(m.can));
  const strictSeparation = manifestQ.data.workspace.strict_production_separation;

  return (
    <div className="admin-page-stack">
      <PageHeader
        title="Roles & permissions"
        description="Built-in workspace roles and what each can do. Assign roles on Members; the API enforces capabilities on every request."
        actions={
          roleAtLeast(role, "admin") ? (
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings/members">Manage members</Link>
            </Button>
          ) : null
        }
      />

      <section className="admin-panel" aria-labelledby="access-your-role-heading">
        <div className="admin-panel-header">
          <h2 id="access-your-role-heading" className="admin-panel-title">
            Your role
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          <div className="admin-settings-row">
            <div className="admin-settings-row-text">
              <Badge
                variant={workspaceRoleBadgeVariant(m.role as WorkspaceRole)}
                className="text-sm"
              >
                {m.role}
              </Badge>
              <p className="admin-settings-row-desc">
                {workspaceRoleDescriptions[m.role as WorkspaceRole]}
              </p>
            </div>
          </div>
        </div>
      </section>

      {canViewMatrix ? (
        <section className="admin-panel" aria-labelledby="access-matrix-heading">
          <div className="admin-panel-header">
            <h2 id="access-matrix-heading" className="admin-panel-title">
              Capability matrix by role
            </h2>
          </div>
          <div className="admin-panel-body-padded" style={{ paddingBottom: 0 }}>
            <p className="admin-settings-row-desc" style={{ margin: "0 0 1rem" }}>
              Effective permissions for each built-in role
              {strictSeparation ? " with strict production separation enabled" : ""}. Your column is
              highlighted.
            </p>
          </div>
          <div className="admin-panel-body">
            <RoleCapabilityMatrix
              strictProductionSeparation={strictSeparation}
              highlightRole={m.role as WorkspaceRole}
            />
          </div>
        </section>
      ) : null}

      <section className="admin-panel" aria-labelledby="access-roles-heading">
        <div className="admin-panel-header">
          <h2 id="access-roles-heading" className="admin-panel-title">
            Role definitions
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          <div className="admin-role-grid">
            {workspaceRolesOrdered.map((r) => (
              <div key={r} className="admin-role-card">
                <div className="admin-role-card-head">
                  <Badge variant={workspaceRoleBadgeVariant(r)}>{r}</Badge>
                </div>
                <p className="admin-role-card-desc">{workspaceRoleDescriptions[r]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="admin-panel" aria-labelledby="access-security-heading">
        <div className="admin-panel-header">
          <h2 id="access-security-heading" className="admin-panel-title">
            Security
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          <div className="admin-settings-row">
            <div className="admin-settings-row-text">
              <p className="admin-settings-row-title">Strict production separation</p>
              <p className="admin-settings-row-desc">
                When enabled, workspace admins cannot approve production requests; only owners and
                approvers can. This changes the matrix above for the admin role.
              </p>
            </div>
            <div className="admin-settings-row-action">
              <Switch
                checked={strictSeparation}
                onCheckedChange={(checked) => {
                  if (!roleAtLeast(role, "owner")) {
                    toast.show({
                      title: "Forbidden",
                      message: "Only owners can change this toggle.",
                      color: "red"
                    });
                    return;
                  }
                  patchStrict.mutate(checked === true);
                }}
                disabled={!roleAtLeast(role, "owner") || patchStrict.isPending}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="admin-panel" aria-labelledby="access-capabilities-heading">
        <div className="admin-panel-header">
          <h2 id="access-capabilities-heading" className="admin-panel-title">
            Your capabilities
          </h2>
        </div>
        <div className="admin-panel-body">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Capability</TableHead>
                <TableHead className="w-[100px]">Allowed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {capabilityKeys.map((key) => (
                <TableRow key={key}>
                  <TableCell>
                    <span className="text-sm">{capabilityLabel(key)}</span>
                    <p
                      className="admin-mono-inline"
                      style={{ margin: "0.125rem 0 0", opacity: 0.6 }}
                    >
                      {key}
                    </p>
                  </TableCell>
                  <TableCell>
                    {m.can[key] ? (
                      <span className="admin-cap-yes">Yes</span>
                    ) : (
                      <span className="admin-cap-no">No</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      {roleAtLeast(role, "owner") ? (
        <section className="admin-panel" aria-labelledby="access-migration-heading">
          <div className="admin-panel-header">
            <h2 id="access-migration-heading" className="admin-panel-title">
              Migration
            </h2>
          </div>
          <div className="admin-panel-body-padded">
            <p className="admin-settings-row-desc" style={{ margin: "0 0 1rem" }}>
              One-time: map legacy <code className="admin-mono-inline">viewer</code> memberships to{" "}
              <code className="admin-mono-inline">auditor</code> for read-only semantics.
            </p>
            <Button
              variant="secondary"
              disabled={migration.isPending}
              onClick={() => {
                migration.mutate(true);
              }}
            >
              {migration.isPending ? <Spinner className="mr-2 h-4 w-4" /> : null}
              Map all viewers to auditors
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );
};
